import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Search, DollarSign, ShieldCheck, Eye, HandHeart, Loader2, Mail, Send } from "lucide-react";

const HOW_IT_WORKS = [
  { icon: Search, title: "Search every licensed option", body: "Enter a location and instantly see licensed assisted living and nursing facilities near you on a live map — not a hand-picked shortlist. You browse, compare availability, and reach out on your own terms." },
  { icon: HandHeart, title: "Reach out directly", body: "When you find a good fit, you contact the facility (or we route your inquiry to them). There's no salesperson in the middle deciding where you should go." },
  { icon: ShieldCheck, title: "Always free for families", body: "Families and hospital discharge planners never pay us a cent, and we never sell your information. Our job is to give you complete, unbiased information." },
];

export default function AboutPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const brand = settings?.brand_name || "CareBed";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your email.";
    if (!form.message.trim()) errs.message = "Please enter a message.";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please complete the required fields."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — your message is on its way. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="about-page">
      <Header />

      <section className="max-w-[1000px] mx-auto px-4 md:px-8 pt-16 pb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-[#0077B6] mb-3">About {brand}</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium text-[#1C211F] leading-tight">{settings?.about_title || "A senior-care directory built for families — not for commissions."}</h1>
        <p className="text-lg text-[#4A534E] mt-5 max-w-2xl">
          {settings?.about_intro}
        </p>
      </section>

      <section className="max-w-[1000px] mx-auto px-4 md:px-8 py-8">
        <h2 className="font-heading text-lg md:text-lg font-medium text-[#1C211F] mb-6">{settings?.about_how_title || `How ${brand} works`}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.title} className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
              <div className="h-11 w-11 rounded-xl bg-[#E3F3ED] flex items-center justify-center mb-4"><s.icon className="h-5 w-5 text-[#0077B6]" /></div>
              <h3 className="font-heading text-lg font-medium text-[#1C211F] mb-2">{s.title}</h3>
              <p className="text-sm text-[#4A534E] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-4 md:px-8 py-8">
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-4"><DollarSign className="h-5 w-5 text-[#0077B6]" /><h2 className="font-heading text-2xl font-medium text-[#1C211F]">{settings?.about_money_title || "How we make money"}</h2></div>
          <p className="text-[#4A534E] leading-relaxed whitespace-pre-line">{settings?.about_money_body}</p>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-2 mb-6"><Eye className="h-5 w-5 text-[#0077B6]" /><h2 className="font-heading text-2xl font-medium text-[#1C211F]">{settings?.about_transparency_title || "Why we're more transparent"}</h2></div>
        <p className="text-[#4A534E] leading-relaxed mb-6 max-w-2xl whitespace-pre-line">
          {settings?.about_transparency_intro}
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
            <h3 className="font-heading text-lg font-medium text-[#9B2C2C] mb-3">The typical referral service</h3>
            <ul className="space-y-2.5 text-sm text-[#4A534E]">
              <li>• Paid a large per-move-in commission by facilities</li>
              <li>• Often shows only facilities that pay them</li>
              <li>• An advisor decides which options you see</li>
              <li>• You can't tell who paid to be recommended</li>
            </ul>
          </div>
          <div className="bg-[#1A365D] text-white rounded-2xl p-6">
            <h3 className="font-heading text-lg font-medium mb-3">{brand}</h3>
            <ul className="space-y-2.5 text-sm text-white/90">
              <li>• Flat, published subscription — no per-placement commissions</li>
              <li>• Shows every licensed facility, free and paid alike</li>
              <li>• You browse and decide; no gatekeeper</li>
              <li>• Paid "Premium" placements are clearly labeled</li>
              <li>• We never sell your personal information</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-4 md:px-8 py-12" id="contact">
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-8 grid md:grid-cols-[1fr_1.2fr] gap-10">
          <div>
            <div className="flex items-center gap-2 mb-3"><Mail className="h-5 w-5 text-[#0077B6]" /><h2 className="font-heading text-2xl font-medium text-[#1C211F]">{settings?.about_contact_title || "Contact us"}</h2></div>
            <p className="text-[#4A534E] leading-relaxed">{settings?.about_contact_intro}</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label htmlFor="contact-name">Your name</Label><Input id="contact-name" required data-testid="contact-name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "contact-name-err" : undefined} value={form.name} onChange={set("name")} className="rounded-xl" />{errors.name && <p id="contact-name-err" className="text-xs text-[#C0392B]">{errors.name}</p>}</div>
              <div className="space-y-1.5"><Label htmlFor="contact-phone">Phone (optional)</Label><Input id="contact-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="rounded-xl" /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="contact-email">Email</Label><Input id="contact-email" required type="email" data-testid="contact-email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "contact-email-err" : undefined} value={form.email} onChange={set("email")} className="rounded-xl" />{errors.email && <p id="contact-email-err" className="text-xs text-[#C0392B]">{errors.email}</p>}</div>
            <div className="space-y-1.5"><Label htmlFor="contact-message">Message</Label><Textarea id="contact-message" required rows={4} data-testid="contact-message" aria-invalid={!!errors.message} aria-describedby={errors.message ? "contact-message-err" : undefined} value={form.message} onChange={set("message")} className="rounded-xl" />{errors.message && <p id="contact-message-err" className="text-xs text-[#C0392B]">{errors.message}</p>}</div>
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send message</>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
